import { useMutation } from '@apollo/client';
import omit from 'lodash.omit';
import { Button } from '@m/alchemy-ui/Button';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { Field } from '@m/alchemy-ui/Field';
import { Input } from '@m/alchemy-ui/Input';
import { Toast } from '@m/alchemy-ui/Toast';
import { Form, Formik, type FormikErrors } from 'formik';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { TranslatableInput } from '../challenge-management/components';
import { DEFAULT_LANGUAGE } from '../shared-constants';
import { type AppProps, type Translation } from '../../common/interfaces';
import { CREATE_CUSTOM_PAGE } from '../../graphql/mutations';
import { getErrorToastMessage } from '../../helpers';
import {
  type CreateCustomPageMutation,
  type CreateCustomPageMutationVariables
} from '../../graphql/mutations/__generated__/page.generated';
import { type ToastMessageType } from '../../common/enums';
import { Actions, CheckboxColumn, ContentRow, IntroText, TitleColumn, Wrapper } from './CustomPage.styled';

const PAGE_TRANSLATE_ID = 'page-title';

const messages = defineMessages({
  intro: {
    id: 'newCustomPageIntro',
    defaultMessage:
      'Custom pages can display elements with text, carousels, images and activity lists in any order you like.'
  },
  pageTitleLabel: {
    id: 'newCustomPagePageTitleLabel',
    defaultMessage: 'Page Title'
  },
  titlePlaceholder: {
    id: 'newCustomPageTitlePlaceholder',
    defaultMessage: "e.g. 'Home', 'F.A.Q.'"
  },
  requiredTitleError: {
    id: 'newCustomPageRequiredTitleError',
    defaultMessage: 'Provide a title in the default language to show this page in the navigation menu.'
  },
  showInNavigationLabel: {
    id: 'newCustomPageShowInNavigationLabel',
    defaultMessage: 'Show in navigation'
  },
  continueButton: {
    id: 'newCustomPageContinueButton',
    defaultMessage: 'Continue'
  },
  submitError: {
    id: 'somethingWentWrong',
    defaultMessage: 'Something went wrong.'
  }
});

interface NewPageFormValues {
  language: string;
  page_navtitle: string;
  showInNavigation: boolean;
  translations: Translation[];
}

export const NewCustomPage = (_props: AppProps) => {
  const intl = useIntl();
  const [toastMessages, setToastMessages] = useState<ToastMessageType[]>([]);
  const [createCustomPage, { loading }] = useMutation<CreateCustomPageMutation, CreateCustomPageMutationVariables>(
    CREATE_CUSTOM_PAGE
  );

  const titleLabel = intl.formatMessage(messages.pageTitleLabel);
  const requiredMessage = intl.formatMessage(messages.requiredTitleError);

  const initialValues: NewPageFormValues = {
    language: DEFAULT_LANGUAGE,
    translations: [],
    page_navtitle: '',
    showInNavigation: true
  };

  const validate = (values: NewPageFormValues) => {
    const errors: FormikErrors<NewPageFormValues> = {};
    setToastMessages([]);
    if (!values.page_navtitle.trim()) {
      errors.page_navtitle = requiredMessage;
    }
    return errors;
  };

  return (
    <Wrapper data-testid="new-page-view">
      <IntroText>{intl.formatMessage(messages.intro)}</IntroText>

      <Formik<NewPageFormValues>
        initialValues={initialValues}
        validate={validate}
        onSubmit={async (values) => {
          try {
            const result = await createCustomPage({
              variables: {
                input: {
                  title: values.page_navtitle,
                  isExclude: !values.showInNavigation,
                  translations:
                    values.translations.length > 0
                      ? values.translations.map((translation) => omit(translation, 'communityId'))
                      : undefined
                }
              }
            });
            const newId = result.data?.createCustomPage;
            if (newId) {
              window.location.href = `/page/admin_edit/${newId}`;
            }
          } catch {
            setToastMessages(getErrorToastMessage(intl.formatMessage(messages.submitError)));
          }
        }}
      >
        {({ errors, values, submitCount, handleBlur, handleChange, setFieldValue }) => {
          const titleError = submitCount > 0 && errors.page_navtitle;

          return (
            <Form>
              <ContentRow>
                <TitleColumn>
                  <Field
                    label={titleLabel}
                    required
                    isFullWidth
                    status={titleError ? { level: 'error', message: errors.page_navtitle } : undefined}
                    input={
                      <TranslatableInput
                        type="Page"
                        field="page_navtitle"
                        translateId={PAGE_TRANSLATE_ID}
                        fieldLabel={titleLabel}
                        id="page_navtitle"
                      >
                        <Input
                          id="page_navtitle"
                          name="page_navtitle"
                          value={values.page_navtitle}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={intl.formatMessage(messages.titlePlaceholder)}
                        />
                      </TranslatableInput>
                    }
                  />
                </TitleColumn>

                <CheckboxColumn>
                  <Checkbox
                    label={intl.formatMessage(messages.showInNavigationLabel)}
                    checked={values.showInNavigation}
                    onChange={(event) => setFieldValue('showInNavigation', event.target.checked)}
                  />
                </CheckboxColumn>
              </ContentRow>

              <Toast messages={toastMessages} />

              <Actions>
                <Button type="submit" priority="primary" isLoading={loading}>
                  {intl.formatMessage(messages.continueButton)}
                </Button>
              </Actions>
            </Form>
          );
        }}
      </Formik>
    </Wrapper>
  );
};
